import { useEffect, useMemo, useState } from 'react';
import { Info, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { TypedConfirmDialog } from '@/components/ui/TypedConfirmDialog';
import { Loader } from '@/components/loader';
import {
  errorMessage,
  tokenApi,
  type PolicyValues,
  type TenantPolicy,
  type TokenSettings,
} from './api';
import { formatDateTime, formatDuration } from './format';
import { TableShell, Td, Th } from './shared';
import { controlClass, useTenantOptions } from './hooks';

type Draft = { [K in keyof PolicyValues]: string } & { event_retention_days: string };

const toDraft = (p: PolicyValues & { event_retention_days?: number }): Draft => ({
  access_token_minutes: String(p.access_token_minutes),
  idle_timeout_minutes: String(p.idle_timeout_minutes),
  max_session_hours: String(p.max_session_hours),
  event_retention_days: String(p.event_retention_days ?? ''),
});

/** Same rules the API enforces, so errors show before saving. */
const validate = (d: Draft, limits: TokenSettings['limits'], withRetention: boolean) => {
  const errors: Partial<Record<keyof Draft, string>> = {};
  const check = (key: keyof Draft, label: string) => {
    const value = Number(d[key]);
    const [low, high] = limits[key];
    if (!Number.isInteger(value) || value < low || value > high) {
      errors[key] = `${label} must be a whole number from ${low} to ${high}`;
    }
  };
  check('access_token_minutes', 'Access token lifetime');
  check('idle_timeout_minutes', 'Idle timeout');
  check('max_session_hours', 'Max session age');
  if (withRetention) check('event_retention_days', 'Retention');

  if (!errors.access_token_minutes && !errors.idle_timeout_minutes) {
    if (Number(d.access_token_minutes) > Number(d.idle_timeout_minutes)) {
      errors.access_token_minutes = 'Cannot exceed the idle timeout';
    }
  }
  if (!errors.idle_timeout_minutes && !errors.max_session_hours) {
    if (Number(d.idle_timeout_minutes) > Number(d.max_session_hours) * 60) {
      errors.idle_timeout_minutes = 'Cannot exceed the max session age';
    }
  }
  return errors;
};

const toValues = (d: Draft): PolicyValues => ({
  access_token_minutes: Number(d.access_token_minutes),
  idle_timeout_minutes: Number(d.idle_timeout_minutes),
  max_session_hours: Number(d.max_session_hours),
});

interface PolicyFieldsProps {
  draft: Draft;
  errors: Partial<Record<keyof Draft, string>>;
  limits: TokenSettings['limits'];
  onChange: (draft: Draft) => void;
  withRetention?: boolean;
}

const PolicyFields = ({ draft, errors, limits, onChange, withRetention }: PolicyFieldsProps) => {
  const field = (key: keyof Draft, label: string, unit: string, help: string) => (
    <InputField
      label={`${label} (${unit})`}
      type="number"
      inputMode="numeric"
      min={limits[key][0]}
      max={limits[key][1]}
      value={draft[key]}
      error={errors[key]}
      helpText={help}
      onChange={(e) => onChange({ ...draft, [key]: e.target.value })}
    />
  );
  return (
    <div className={`grid grid-cols-1 items-start gap-4 ${withRetention ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
      {field('access_token_minutes', 'Access token', 'min', 'How often applications must refresh.')}
      {field('idle_timeout_minutes', 'Idle timeout', 'min', 'Session ends after this long without a refresh.')}
      {field('max_session_hours', 'Max session age', 'hrs', 'Hard limit from sign-in, however active.')}
      {withRetention &&
        field('event_retention_days', 'Event retention', 'days', 'How long token activity is kept before it is deleted.')}
    </div>
  );
};

const Summary = ({ draft, errors }: { draft: Draft; errors: Partial<Record<keyof Draft, string>> }) => {
  if (Object.keys(errors).length) return null;
  const v = toValues(draft);
  return (
    <p className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-lg px-3 py-2">
      <Info className="w-4 h-4 shrink-0 mt-px" />
      <span>
        Tokens last {formatDuration(v.access_token_minutes)}. A user who stays active is renewed each time and signed out
        after {formatDuration(v.idle_timeout_minutes)} of inactivity, or {formatDuration(v.max_session_hours * 60)} after
        signing in at the latest.
      </span>
    </p>
  );
};

export const SettingsTab = () => {
  const tenants = useTenantOptions();
  const [settings, setSettings] = useState<TokenSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDefault, setConfirmDefault] = useState(false);

  const [editing, setEditing] = useState<{ tenantId: string; draft: Draft } | null>(null);
  const [toRemove, setToRemove] = useState<TenantPolicy | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (next: TokenSettings) => {
    setSettings(next);
    setDraft(toDraft(next.default));
  };

  useEffect(() => {
    tokenApi
      .settings()
      .then(apply)
      .catch((err) => toast.error(errorMessage(err, 'Failed to load token settings')));
  }, []);

  const defaultErrors = useMemo(
    () => (settings && draft ? validate(draft, settings.limits, true) : {}),
    [settings, draft],
  );
  const overrideErrors = useMemo(
    () => (settings && editing ? validate(editing.draft, settings.limits, false) : {}),
    [settings, editing],
  );

  const availableTenants = useMemo(() => {
    const used = new Set(settings?.tenants.map((t) => t.tenant_id));
    return tenants.filter((t) => !used.has(t.id));
  }, [tenants, settings]);

  if (!settings || !draft) {
    return (
      <div className="py-24 flex justify-center">
        <Loader />
      </div>
    );
  }

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(toDraft(settings.default));

  const saveDefault = async () => {
    setSaving(true);
    try {
      apply(await tokenApi.saveDefault({ ...toValues(draft), event_retention_days: Number(draft.event_retention_days) }));
      toast.success('Default token settings saved');
      setConfirmDefault(false);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const saveOverride = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      apply(await tokenApi.saveTenant(editing.tenantId, toValues(editing.draft)));
      toast.success('Tenant override saved');
      setEditing(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save tenant override'));
    } finally {
      setBusy(false);
    }
  };

  const removeOverride = async () => {
    if (!toRemove) return;
    setBusy(true);
    try {
      apply(await tokenApi.deleteTenant(toRemove.tenant_id));
      toast.success('Override removed, tenant now uses the defaults');
      setToRemove(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to remove override'));
    } finally {
      setBusy(false);
    }
  };

  const startOverride = (tenantId: string, policy?: PolicyValues) =>
    setEditing({ tenantId, draft: toDraft(policy ?? settings.default) });

  return (
    <div className="space-y-8">
      <Card
        title="Platform defaults"
        subtitle={
          settings.default.source === 'saved'
            ? `Last changed by ${settings.default.updated_by ?? 'unknown'} on ${formatDateTime(settings.default.updated_at)}`
            : 'Using values from the server environment until you save'
        }
      >
        <div className="space-y-4">
          <PolicyFields draft={draft} errors={defaultErrors} limits={settings.limits} onChange={setDraft} withRetention />
          <Summary draft={draft} errors={defaultErrors} />
          <p className="text-xs text-slate-500">
            Changes apply to every new sign-in and refresh, in all tenants without an override. Existing refresh tokens
            keep their current expiry until they are next renewed.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(toDraft(settings.default))}>
              Reset
            </Button>
            <Button disabled={!dirty || Object.keys(defaultErrors).length > 0} onClick={() => setConfirmDefault(true)}>
              Save defaults
            </Button>
          </div>
        </div>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Tenant overrides</h3>
            <p className="text-xs text-slate-500">A tenant with an override ignores the platform defaults.</p>
          </div>
          <Button
            size="sm"
            className="gap-2"
            disabled={availableTenants.length === 0}
            onClick={() => startOverride(availableTenants[0]?.id)}
          >
            <Plus className="w-4 h-4" /> Add override
          </Button>
        </div>

        <TableShell>
          <thead className="border-b border-slate-100 dark:border-slate-800">
            <tr>
              <Th>Tenant</Th>
              <Th>Access token</Th>
              <Th>Idle timeout</Th>
              <Th>Max session</Th>
              <Th>Changed</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {settings.tenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                  No overrides. Every tenant uses the platform defaults.
                </td>
              </tr>
            ) : (
              settings.tenants.map((t) => (
                <tr key={t.tenant_id}>
                  <Td className="font-semibold">{t.tenant_name ?? t.tenant_slug ?? t.tenant_id}</Td>
                  <Td>{formatDuration(t.access_token_minutes)}</Td>
                  <Td>{formatDuration(t.idle_timeout_minutes)}</Td>
                  <Td>{formatDuration(t.max_session_hours * 60)}</Td>
                  <Td className="text-xs text-slate-500">
                    {t.updated_by ?? '—'}
                    <span className="block">{formatDateTime(t.updated_at)}</span>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" aria-label={`Edit ${t.tenant_name}`} onClick={() => startOverride(t.tenant_id, t)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={`Remove override for ${t.tenant_name}`} onClick={() => setToRemove(t)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </TableShell>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl shadow-2xl" title="Tenant override" subtitle="Applies only to this tenant">
            <div className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="override-tenant">
                  Tenant
                </label>
                <select
                  id="override-tenant"
                  value={editing.tenantId}
                  onChange={(e) => setEditing({ ...editing, tenantId: e.target.value })}
                  disabled={settings.tenants.some((t) => t.tenant_id === editing.tenantId)}
                  className={controlClass}
                >
                  {tenants
                    .filter(
                      (t) =>
                        t.id === editing.tenantId || availableTenants.some((a) => a.id === t.id),
                    )
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </div>
              <PolicyFields
                draft={editing.draft}
                errors={overrideErrors}
                limits={settings.limits}
                onChange={(d) => setEditing({ ...editing, draft: d })}
              />
              <Summary draft={editing.draft} errors={overrideErrors} />
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  onClick={saveOverride}
                  isLoading={busy}
                  disabled={!editing.tenantId || Object.keys(overrideErrors).length > 0}
                >
                  Save override
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {confirmDefault && (
        <TypedConfirmDialog
          title="Change platform defaults"
          variant="primary"
          confirmText="apply"
          confirmLabel="Apply to all tenants"
          isLoading={saving}
          onClose={() => setConfirmDefault(false)}
          onConfirm={saveDefault}
          description="These lifetimes will apply to every new sign-in and refresh in all tenants that have no override. Applications connected to the platform will refresh on the new schedule."
        />
      )}

      {toRemove && (
        <TypedConfirmDialog
          title="Remove tenant override"
          confirmText={toRemove.tenant_slug ?? toRemove.tenant_name ?? 'remove'}
          confirmLabel="Remove override"
          isLoading={busy}
          onClose={() => setToRemove(null)}
          onConfirm={removeOverride}
          description={
            <>
              <span className="font-bold text-slate-900 dark:text-white">{toRemove.tenant_name}</span> will go back to the
              platform defaults for new sign-ins and refreshes.
            </>
          }
        />
      )}
    </div>
  );
};
